
import { defineComponent } from "vue";
import Swal from "sweetalert2/dist/sweetalert2.js";
import ReelVoicePicker from "@/components/social/ReelVoicePicker.vue";
import SocialAccountAvatar from "@/components/social/SocialAccountAvatar.vue";
import { createContentSeries, syncSocialAccounts } from "@/api/ghlContent.api.js";
import {
  accountDisplayName,
  accountOptionKey,
  getPlatformMeta,
  uniquePlatformsFromAccounts
} from "@/utils/socialPlatformMeta.js";
import { getReelMusic, getReelVoices } from "@/api/ghlAi.api.js";
import {
  CAPTION_CASING_OPTIONS,
  CAPTION_POSITION_OPTIONS,
  CAPTION_PRESETS,
  CAPTION_SIZE_OPTIONS,
  CAPTION_STYLE_DEFAULTS,
  getCaptionPreviewText,
  getCaptionStyleSummary,
  normalizeCaptionStyle
} from "@/utils/captionStyleCatalog.js";

const DEFAULT_REEL_PREVIEW_BASE = "https://images.empoweredconsumerism-static-files.com/reel-voices/v2";

const REEL_TONES = [
  {
    id: "educator",
    label: "Warm educator",
    description: "Calm, clear, and trustworthy — like sharing a helpful tip with a friend."
  },
  {
    id: "upbeat",
    label: "Bright & upbeat",
    description: "Friendly and energetic, with a light smile in the voice."
  },
  {
    id: "professional",
    label: "Professional",
    description: "Clear, confident, and polished — like a short brand message."
  }
];

function getRequestErrorMessage(error: unknown, fallback: string) {
  const candidate = error as { response?: { data?: { message?: unknown } } };
  if (candidate.response && candidate.response.data && candidate.response.data.message) {
    return String(candidate.response.data.message);
  }
  return fallback;
}

export default defineComponent({
  name: "FillMyCalendarDialog",
  components: { ReelVoicePicker, SocialAccountAvatar },
  props: {
    modelValue: {
      type: Boolean,
      default: false
    }
  },
  emits: ["update:modelValue", "started"],
  data() {
    return {
      durationOptions: [7, 14, 30],
      durationDays: 7,
      topic: "",
      postTimeLocal: "09:00",
      languageCode: "en",
      connectedAccounts: [] as any[],
      selectedAccountKeys: [] as string[],
      selectedPlatforms: [] as string[],
      voiceId: "",
      toneId: "educator",
      musicTrackId: null as number | null,
      voices: [] as any[],
      musicTracks: [] as any[],
      voiceExpanded: false,
      toneExpanded: false,
      musicExpanded: false,
      captionExpanded: false,
      reelCaptionStyle: { ...CAPTION_STYLE_DEFAULTS },
      captionPresets: CAPTION_PRESETS,
      captionSizeOptions: CAPTION_SIZE_OPTIONS,
      captionPositionOptions: CAPTION_POSITION_OPTIONS,
      captionCasingOptions: CAPTION_CASING_OPTIONS,
      autoPublish: true,
      submitting: false,
      formError: "",
      reelTones: REEL_TONES,
      reelPreviewBase: DEFAULT_REEL_PREVIEW_BASE,
      reelPreviewAudio: null as HTMLAudioElement | null
    };
  },
  beforeUnmount() {
    this.stopVoicePreview();
  },
  computed: {
    visible: {
      get(): boolean {
        return this.modelValue;
      },
      set(value: boolean) {
        this.$emit("update:modelValue", value);
      }
    },
    selectedAccounts(): any[] {
      return this.connectedAccounts.filter((account) =>
        this.selectedAccountKeys.includes(accountOptionKey(account))
      );
    },
    filteredVoices(): any[] {
      return this.voices;
    },
    selectedVoiceLabel(): string {
      const voice = this.voices.find((entry) => entry.id === this.voiceId);
      return voice ? String(voice.label || voice.name || voice.id) : "Choose a voice";
    },
    selectedToneLabel(): string {
      const tone = REEL_TONES.find((entry) => entry.id === this.toneId);
      return tone ? tone.label : "Warm educator";
    },
    selectedMusicLabel(): string {
      if (this.musicTrackId == null) {
        return "No music";
      }
      const track = this.musicTracks.find((entry) => entry.id === this.musicTrackId);
      return track ? String(track.title || `Track ${track.id}`) : "Music selected";
    },
    selectedCaptionStyleLabel(): string {
      return getCaptionStyleSummary(this.reelCaptionStyle);
    },
    captionStylePreviewText(): string {
      return getCaptionPreviewText(this.reelCaptionStyle.casing);
    },
    captionStylePreviewClasses(): string[] {
      const style = normalizeCaptionStyle(this.reelCaptionStyle);
      return [
        `social-post-page__caption-preview-text--${style.preset}`,
        `social-post-page__caption-preview-text--size-${style.size}`,
        `social-post-page__caption-preview-text--position-${style.position}`
      ];
    }
  },
  watch: {
    modelValue(open: boolean) {
      if (open) {
        this.resetForm();
        this.loadOptions();
      }
    }
  },
  methods: {
    resetForm() {
      this.formError = "";
      this.durationDays = 7;
      this.topic = "";
      this.postTimeLocal = "09:00";
      this.languageCode = "en";
      this.reelCaptionStyle = { ...CAPTION_STYLE_DEFAULTS };
      this.autoPublish = true;
      this.submitting = false;
    },
    onClosed() {
      this.formError = "";
      this.stopVoicePreview();
    },
    stopVoicePreview() {
      if (this.reelPreviewAudio) {
        this.reelPreviewAudio.pause();
        this.reelPreviewAudio = null;
      }
    },
    playMusicPreview(track: any) {
      const url = String(track?.previewUrl || "").trim();
      if (!url || !/^https?:\/\//i.test(url)) {
        return;
      }
      this.stopVoicePreview();
      const audio = new Audio(url);
      this.reelPreviewAudio = audio;
      audio.play().catch(() => {
        Swal.fire({
          icon: "info",
          title: "Preview unavailable",
          text: "Music preview could not be loaded.",
          confirmButtonClass: "btn btn-secondary"
        });
      });
    },
    playVoicePreview(voice: any, toneId?: string) {
      const selectedVoice = voice || this.voices.find((entry) => entry.id === this.voiceId);
      const resolvedVoiceId = String(selectedVoice?.id || this.voiceId || "").trim();
      const resolvedTone = String(toneId || this.toneId || "educator").trim();
      const voicePreviewUrl = String(selectedVoice?.previewUrl || "").trim();
      const usesHostedReelPreview = voicePreviewUrl.includes("/reel-voices/v");
      const url = voicePreviewUrl && /^https?:\/\//i.test(voicePreviewUrl) && !usesHostedReelPreview
        ? voicePreviewUrl
        : (() => {
          const base = voicePreviewUrl
            ? voicePreviewUrl.replace(/\/[^/]+\.mp3$/i, "")
            : String(this.reelPreviewBase || "").replace(/\/$/, "");
          return resolvedVoiceId && base
            ? `${base}/${resolvedVoiceId}-${resolvedTone}.mp3`
            : voicePreviewUrl;
        })();
      if (!url) {
        return;
      }
      this.stopVoicePreview();
      const audio = new Audio(url);
      this.reelPreviewAudio = audio;
      audio.play().catch(() => {
        Swal.fire({
          icon: "info",
          title: "Preview unavailable",
          text: "Voice preview could not be loaded.",
          confirmButtonClass: "btn btn-secondary"
        });
      });
    },
    getPlatformMeta,
    accountDisplayName,
    accountOptionKey,
    onAccountsChange() {
      this.selectedPlatforms = uniquePlatformsFromAccounts(this.selectedAccounts);
    },
    ensureVoiceSelection() {
      if (this.voiceId && this.voices.some((voice) => voice.id === this.voiceId)) {
        return;
      }
      const first = this.voices[0];
      this.voiceId = first ? String(first.id) : "";
    },
    async loadOptions() {
      this.formError = "";
      try {
        const [accountsResponse, voicesResponse, musicResponse] = await Promise.all([
          syncSocialAccounts(),
          getReelVoices(),
          getReelMusic()
        ]);
        const accounts = Array.isArray(accountsResponse.data?.accounts)
          ? accountsResponse.data.accounts
          : [];
        this.connectedAccounts = accounts.filter(
          (account: any) => account.status !== "disconnected"
        );
        this.onAccountsChange();
        const voices = voicesResponse.data?.voices || [];
        this.voices = voices;
        this.musicTracks = musicResponse.data?.tracks || [];
        if (voicesResponse.data?.previewBase) {
          this.reelPreviewBase = String(voicesResponse.data.previewBase).replace(/\/$/, "");
        } else if (voices[0]?.previewUrl) {
          this.reelPreviewBase = String(voices[0].previewUrl).replace(/\/[^/]+$/, "");
        } else {
          this.reelPreviewBase = DEFAULT_REEL_PREVIEW_BASE;
        }
        this.ensureVoiceSelection();
      } catch {
        this.formError = "Unable to load options. Please try again.";
      }
    },
    async submit() {
      this.formError = "";
      const topic = this.topic.trim();
      if (topic.length < 10) {
        this.formError = "Add a topic with at least 10 characters.";
        return;
      }
      if (!this.selectedAccountKeys.length) {
        this.formError = "Select at least one social account.";
        return;
      }
      if (!this.voiceId) {
        this.formError = "Choose a voice.";
        return;
      }
      this.submitting = true;
      try {
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
        const payload: Record<string, unknown> = {
          durationDays: this.durationDays,
          topic,
          postTimeLocal: this.postTimeLocal,
          timezone,
          languageCode: this.languageCode,
          voiceId: this.voiceId,
          toneId: this.toneId,
          platforms: this.selectedPlatforms,
          accounts: this.selectedAccounts.map((account) => ({
            platform: account.platform,
            accountId: account.accountId
          }))
        };
        if (this.musicTrackId != null) {
          payload.musicTrackId = this.musicTrackId;
        }
        payload.captionStyle = normalizeCaptionStyle(this.reelCaptionStyle);
        payload.autoPublish = this.autoPublish;
        const response = await createContentSeries(payload);
        const series = response.data?.series;
        this.visible = false;
        this.$emit("started", series);
      } catch (e) {
        this.formError = getRequestErrorMessage(e, "Unable to start draft generation.");
      } finally {
        this.submitting = false;
      }
    }
  }
});
